import React, { CSSProperties } from "react";
import { SectionHeader } from "@/shell/components/SectionHeader";
import { ContactsIntelligenceResponse } from "@/features/orders-analytics/types/orders-analytics.types";
import { formatters } from "@/features/orders-analytics/utils/formatters";

type Props = {
    data: ContactsIntelligenceResponse;
};

const tiles = (data: ContactsIntelligenceResponse): { label: string; value: string; sub: string }[] => [
    {
        label: "Contacts (address book)",
        value: formatters.formatNumber(data.totalContacts),
        sub: `${formatters.formatNumber(data.distinctEmails)} distinct emails`,
    },
    {
        label: "Accounts with contacts",
        value: formatters.formatNumber(data.accountsWithContacts),
        sub: `${formatters.formatNumber(data.avgContactsPerAccount)} contacts / account`,
    },
    {
        label: "Country coverage",
        value: formatters.formatPercentage(data.countryCoverage),
        sub: "contacts with a country",
    },
    {
        label: "Compliance coverage",
        value: formatters.formatPercentage(data.complianceCoverage),
        sub: `${formatters.formatPercentage(data.approvedShare)} approved`,
    },
];

const maxContacts = (data: ContactsIntelligenceResponse): number =>
    data.topCountries.reduce((max, row) => (row.contacts > max ? row.contacts : max), 0);

export const CounterpartiesSection: React.FC<Props> = ({ data }) => {
    const top = maxContacts(data);

    return (
        <section className="section">
            <SectionHeader
                title="Counterparties & address book"
                meta="who accounts send to (beneficiaries), from contacts-read"
            />

            <div className="grid-4">
                {tiles(data).map((tile) => (
                    <div key={tile.label} className="card tile">
                        <div className="tile__label">{tile.label}</div>
                        <div className="tile__value num">{tile.value}</div>
                        <div className="tile__sub">{tile.sub}</div>
                    </div>
                ))}
            </div>

            <div className="grid-12" style={{ marginTop: 12 }}>
                <div style={{ gridColumn: "span 7" }}>
                    <div className="card ci-table-card">
                        <div className="ci-scroll">
                            <table className="ci-tbl">
                                <thead>
                                    <tr>
                                        <th scope="col">Counterparty country</th>
                                        <th scope="col" className="num">Contacts</th>
                                        <th scope="col">Share of top</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.topCountries.map((row) => (
                                        <tr key={row.country}>
                                            <td style={{ fontWeight: 600 }}>{row.country}</td>
                                            <td className="num">{formatters.formatNumber(row.contacts)}</td>
                                            <td>
                                                <div className="ci-bar">
                                                    <div className="ci-bar__track">
                                                        <div
                                                            className="ci-bar__fill"
                                                            style={{
                                                                width: `${top > 0 ? (row.contacts / top) * 100 : 0}%`,
                                                            }}
                                                        />
                                                    </div>
                                                    <span className="ci-bar__value">
                                                        {formatters.formatNumber(row.contacts)}
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div style={{ gridColumn: "span 5" }}>
                    <div className="card" style={joinCardStyle}>
                        <div className="tile__label">Operations → contacts link</div>
                        <div className="tile__value num" style={{ marginTop: 6 }}>
                            {formatters.formatPercentage(data.contactEmailCoverage)}
                        </div>
                        <div className="tile__sub">
                            of {formatters.formatNumber(data.completedOrders)} completed orders carry a
                            contactEmail
                        </div>
                        <div style={{ marginTop: 10, fontSize: 12, color: "var(--vb-fg-strong)" }}>
                            {formatters.formatNumber(data.counterpartiesPaid)} distinct counterparties matched
                            (a floor, not an activation rate)
                        </div>
                        <ul style={warnListStyle}>
                            {data.warnings.map((warning) => (
                                <li key={warning} style={{ marginBottom: 4 }}>
                                    {warning}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
};

const joinCardStyle: CSSProperties = {
    padding: 16,
    borderLeft: "3px solid var(--vb-warning-500)",
};

const warnListStyle: CSSProperties = {
    margin: "12px 0 0 0",
    paddingLeft: 16,
    fontSize: 11,
    color: "var(--vb-fg-muted)",
    lineHeight: 1.5,
};
